import React, { useState } from "react";
import * as R from "ramda";
import { Redirect } from "react-router-dom";
import { Query } from "react-apollo";
import { Tunnel } from "react-tunnels";
import { css } from "styled-components";

import { withApolloState } from "@arachnys/flow-app/src/util/apollo";
import { decodeId } from "@arachnys/flow-app/src/util";
import { BackButtonTopbar, Title } from "@arachnys/styleguide/PageHeader";
import Heading from "@arachnys/styleguide/Heading";

import { Comments } from "@arachnys/flow-app/src/component/Comments";
import { GET_BULK_REQUEST, GET_BULK_REQUEST_FILES } from "../queries";

import { canViewDetails } from "../BulkUploads";
import DetailsFilesTable from "./DetailsFilesTable";
import AdditionalFileUploader from "./AdditionalFileUploader";
import ChangeStatusButton from "./ChangeStatusButton";
import { statuses, commentsTags, fileTypes } from "./config";

const styles = {
    uploaderContainer: css`
        margin: 20px 0;
    `,
    commentsContainer: css`
        margin: 20px 0;
    `,
};

const CommentsContainer = ({ requestId }) => {
    return (
        <>
            <Heading level={1}>Comments</Heading>
            <div css={styles.commentsContainer}>
                <Comments roomID={requestId} tags={commentsTags} />
            </div>
        </>
    );
};

const FilesTable = ({
    requestId,
    refetchArray = [],
    fileType,
    refetchable = false,
    setQty = () => {},
    requestStatus,
}) => (
    <Query
        query={GET_BULK_REQUEST_FILES}
        variables={{
            id: requestId,
        }}
        fetchPolicy="cache-and-network"
    >
        {withApolloState(({ data, refetch }) => {
            if (refetchable) {
                refetchArray[0] = refetch;
            }
            const fileData = R.mapObjIndexed(
                type =>
                    data.bulkUploadRequestFiles.filter(
                        file => file.fileType === type
                    ),
                fileTypes
            );
            setQty(R.mapObjIndexed(files => files.length, fileData));

            return (
                <DetailsFilesTable
                    refetch={refetch}
                    requestStatus={requestStatus}
                    data={fileData[R.invertObj(fileTypes)[fileType]]}
                />
            );
        })}
    </Query>
);

const BulkRequestDetailsPage = ({
    match: {
        params: { id },
    },
}) => {
    const filesRefetchArray = [() => {}];

    const uploadedFilesQty = {
        SANITIZED: 0,
        REPORT: 0,
    };
    const setUploadedFilesQty = ({ SANITIZED, REPORT }) => {
        uploadedFilesQty.SANITIZED = SANITIZED;
        uploadedFilesQty.REPORT = REPORT;
    };

    const [isBusy, setBusy] = useState(false);
    const requestId = decodeId(id);

    return (
        <Query
            query={GET_BULK_REQUEST}
            variables={{ id }}
            fetchPolicy="network-only"
        >
            {withApolloState(
                ({ data: { bulkUploadRequest, ticketingPermissions } }) => {
                    const permissions = ticketingPermissions.bulkUploadRequest;
                    const status = bulkUploadRequest.status;
                    const isSubmittedState = status === statuses.SUBMITTED;
                    const fileType = isSubmittedState
                        ? fileTypes.SANITIZED
                        : fileTypes.REPORT;

                    return (
                        <>
                            {!canViewDetails(permissions) && (
                                <Redirect to="/" />
                            )}
                            <Tunnel id="bluebar-left-content">
                                <BackButtonTopbar link="/tickets/bulk/">
                                    <div>
                                        <Title>{bulkUploadRequest.name}</Title>
                                        <div>
                                            <div>
                                                Notes: {bulkUploadRequest.notes}
                                            </div>
                                            {isSubmittedState && (
                                                <span>
                                                    Request is submitted. Please
                                                    upload sanitized file and
                                                    press &quot;Accept&quot; to
                                                    accept it
                                                </span>
                                            )}
                                            {status === statuses.ACCEPTED && (
                                                <span>
                                                    Request is accepted. Please
                                                    upload report file and press
                                                    &quot;Complete&quot; to
                                                    complete it
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </BackButtonTopbar>
                            </Tunnel>
                            <Heading level={1}>Details</Heading>
                            <FilesTable
                                requestId={requestId}
                                fileType={fileTypes.DRAFT}
                            />
                            <Heading level={1}>
                                {isSubmittedState
                                    ? "Sanitized Files"
                                    : "Reports"}
                            </Heading>
                            <FilesTable
                                requestId={requestId}
                                fileType={fileType}
                                refetchable
                                refetchArray={filesRefetchArray}
                                setQty={setUploadedFilesQty}
                                requestStatus={status}
                            />
                            <div css={styles.uploaderContainer}>
                                <AdditionalFileUploader
                                    type={fileType}
                                    refetchArray={filesRefetchArray}
                                    requestId={requestId}
                                />
                            </div>
                            <CommentsContainer requestId={requestId} />
                            <div>
                                {isSubmittedState && (
                                    <ChangeStatusButton
                                        action={statuses.ACCEPTED}
                                        requestId={requestId}
                                        isAccept
                                        setBusy={setBusy}
                                        disabled={isBusy}
                                        filesQty={uploadedFilesQty}
                                    >
                                        Accept
                                    </ChangeStatusButton>
                                )}
                                {status === statuses.ACCEPTED && (
                                    <>
                                        <ChangeStatusButton
                                            action={statuses.SUBMITTED}
                                            requestId={requestId}
                                            setBusy={setBusy}
                                            disabled={isBusy}
                                            filesQty={uploadedFilesQty}
                                        >
                                            Reject
                                        </ChangeStatusButton>
                                        <ChangeStatusButton
                                            action={statuses.COMPLETED}
                                            requestId={requestId}
                                            isAccept
                                            setBusy={setBusy}
                                            disabled={isBusy}
                                            filesQty={uploadedFilesQty}
                                        >
                                            Complete
                                        </ChangeStatusButton>
                                    </>
                                )}
                                {status === statuses.COMPLETED &&
                                    permissions.canRelease && (
                                        <>
                                            <ChangeStatusButton
                                                action={statuses.ACCEPTED}
                                                requestId={requestId}
                                                setBusy={setBusy}
                                                disabled={isBusy}
                                                filesQty={uploadedFilesQty}
                                            >
                                                Reject
                                            </ChangeStatusButton>
                                            <ChangeStatusButton
                                                action={statuses.RELEASED}
                                                requestId={requestId}
                                                isAccept
                                                setBusy={setBusy}
                                                disabled={isBusy}
                                                filesQty={uploadedFilesQty}
                                            >
                                                Release
                                            </ChangeStatusButton>
                                        </>
                                    )}
                            </div>
                        </>
                    );
                }
            )}
        </Query>
    );
};

export default BulkRequestDetailsPage;
